import React, { useState, useEffect } from "react";
import {
  Users,
  FileText,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  Activity,
  BarChart3,
  PieChart,
  TrendingDown,
} from "lucide-react";
import apiService from "../../services/apiService";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await apiService.makeRequest("/dashboard/admin-stats");
      setStats(response);
    } catch (err) {
      console.error("Failed to load dashboard stats:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      title: "Total Members",
      value: stats.total_members,
      icon: Users,
      color: "blue",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      iconColor: "text-blue-600",
    },
    {
      title: "Active Claims",
      value: stats.active_claims,
      icon: FileText,
      color: "green",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      iconColor: "text-green-600",
    },
    {
      title: "Pending Payments",
      value: stats.pending_payments,
      icon: DollarSign,
      color: "yellow",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      iconColor: "text-yellow-600",
    },
    {
      title: "Fraud Alerts",
      value: stats.fraud_alerts,
      icon: AlertTriangle,
      color: "red",
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      iconColor: "text-red-600",
    },
  ];

  // Prepare chart data
  const claimsData = {
    processed: stats.monthly_stats.claims_processed,
    active: stats.active_claims,
    fraudDetected: stats.monthly_stats.fraud_detected,
  };

  const paymentData = {
    made: stats.monthly_stats.payments_made,
    pending: stats.pending_payments,
  };

  // Calculate percentages for visualizations
  const totalClaims = claimsData.processed + claimsData.active;
  const totalPayments = paymentData.made + paymentData.pending;

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Admin Dashboard
        </h2>
        <p className="text-gray-600">
          Overview of system performance and key metrics
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">
                    {card.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {card.value.toLocaleString()}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${card.bgColor}`}>
                  <Icon className={`h-6 w-6 ${card.iconColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Claims Processing Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Claims Overview
            </h3>
            <BarChart3 className="h-5 w-5 text-gray-500" />
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Claims Processed
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {claimsData.processed}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: totalClaims > 0 ? `${(claimsData.processed / totalClaims) * 100}%` : "0%",
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Active Claims
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {claimsData.active}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: totalClaims > 0 ? `${(claimsData.active / totalClaims) * 100}%` : "0%",
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Fraud Detected
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {claimsData.fraudDetected}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-red-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: totalClaims > 0 ? `${(claimsData.fraudDetected / totalClaims) * 100}%` : "0%",
                  }}
                ></div>
              </div>
            </div>

            <div className="pt-4 border-t mt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Avg Processing Time
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {stats.monthly_stats.avg_processing_time} days
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Distribution Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Payment Status
            </h3>
            <PieChart className="h-5 w-5 text-gray-500" />
          </div>
          <div className="space-y-6">
            {/* Donut Chart Visualization */}
            <div className="flex items-center justify-center">
              <div className="relative w-48 h-48">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  {/* Background circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="20"
                  />
                  {/* Payments Made */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="20"
                    strokeDasharray={`${totalPayments > 0 ? (paymentData.made / totalPayments) * 251.2 : 0} 251.2`}
                    strokeLinecap="round"
                  />
                  {/* Pending Payments */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="20"
                    strokeDasharray={`${totalPayments > 0 ? (paymentData.pending / totalPayments) * 251.2 : 0} 251.2`}
                    strokeDashoffset={`-${totalPayments > 0 ? (paymentData.made / totalPayments) * 251.2 : 0}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{totalPayments}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-700">
                    Payments Made
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {paymentData.made} ({totalPayments > 0 ? Math.round((paymentData.made / totalPayments) * 100) : 0}%)
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-700">
                    Pending Payments
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {paymentData.pending} ({totalPayments > 0 ? Math.round((paymentData.pending / totalPayments) * 100) : 0}%)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Activity
        </h3>
        {stats.recent_activities && stats.recent_activities.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_activities.map((activity, index) => {
              const getActivityIcon = () => {
                switch (activity.type) {
                  case "claim":
                    return <FileText className="h-4 w-4 text-blue-600" />;
                  case "payment":
                    return <DollarSign className="h-4 w-4 text-green-600" />;
                  case "fraud":
                    return <AlertTriangle className="h-4 w-4 text-red-600" />;
                  case "member":
                    return <Users className="h-4 w-4 text-purple-600" />;
                  default:
                    return <Activity className="h-4 w-4 text-blue-600" />;
                }
              };

              return (
                <div
                  key={index}
                  className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="mt-0.5 mr-3">{getActivityIcon()}</div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No recent activities</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;